/**
 * Lane C0-PROPS round 3: the plains kit's standing night lights (render/lighting.ts `registerNightLights`): every
 * lantern post, lantern string, lantern pole, palace and stone lantern, torch, brazier, guard stand and shrine the kit
 * places, and the windows and doors along the fronts of the kit's buildings. Only on the 256 m plains (`kitMap`).
 */
import { hash2, type GameMap } from '@nine/shared';
import { registerNightLights, type NightLight } from '../lighting';
import { PLAINS_DRESSING } from './dressing';
import { kitMap } from './index';
import { PLAINS_PROPS } from './placement';

/** Light height (art px), pool radius (px), flame or not, lights along a string (tiles from the middle). */
const LIGHTS: Record<string, { lift: number; r: number; flame?: boolean; along?: readonly number[] }> = {
  lanternPost: { lift: 50, r: 30 },
  lanternString: { lift: 44, r: 20, along: [-1, -0.5, 0, 0.5, 1] },
  lanternPole2: { lift: 66, r: 34 },
  palaceLantern: { lift: 26, r: 26 },
  pagodaLantern: { lift: 34, r: 24 },
  stoneLantern: { lift: 22, r: 22 },
  torchPost: { lift: 46, r: 34, flame: true },
  brazier: { lift: 18, r: 30, flame: true },
  guardStand: { lift: 18, r: 26, flame: true },
  gatePost: { lift: 34, r: 18 },
  incense: { lift: 20, r: 16, flame: true },
  altar: { lift: 22, r: 20, flame: true },
  roadShrine: { lift: 22, r: 16 },
  wallGate: { lift: 46, r: 22 },
  campfire: { lift: 14, r: 34, flame: true },
};

function kitLights(m: GameMap): readonly NightLight[] {
  if (!kitMap(m)) return [];
  const out: NightLight[] = [];
  for (const q of [...PLAINS_PROPS, ...PLAINS_DRESSING]) {
    const L = LIGHTS[q.id];
    if (!L) continue;
    if (L.along) for (const t of L.along) out.push({ x: q.at[0] + t * 1.2, y: q.at[1], lift: L.lift - Math.round((1 - t * t) * 6), r: L.r, squash: 0.7 });
    else out.push({ x: q.at[0], y: q.at[1], lift: L.lift, r: L.r, flicker: L.flame });
  }
  // Windows and doors: warm glow along each building's two near fronts (none for tents and stalls).
  for (const st of m.structures) {
    if (!st.type || st.type === 'tent' || st.type === 'stall') continue;
    const two = st.type === 'inn' || st.type === 'hall';
    for (let u = 1; u < st.w; u += 2) {
      out.push({ x: st.i + u + 0.5, y: st.j + st.h + 0.2, lift: 24, r: 16, squash: 0.8, warm: 0.7 });
      if (two && hash2(st.i + u, st.j) < 0.6) out.push({ x: st.i + u + 0.5, y: st.j + st.h + 0.2, lift: 84, r: 14, squash: 0.8, warm: 0.6 });
    }
    for (let u = 1; u < st.h; u += 3) out.push({ x: st.i + st.w + 0.2, y: st.j + u + 0.5, lift: 26, r: 14, squash: 0.8, warm: 0.5 });
  }
  return out;
}

registerNightLights(kitLights);
